import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  linkedSignal,
  model,
  Signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';
import type { FormValueControl } from '@angular/forms/signals';
import { UiFieldControl } from '../../../basics/ui-field-control';
import { getRandomId } from '../../../utils/get-random-id';
import { UiInputFocusDirective } from '../../../directives';
import { ValidationErrorUIComponent } from '../validation-error/validation-error.component';
import { IconUIComponent } from '../icon/icon.component';
import { IconsInitConfigType } from '../../../constants';
import { InputAutocompleteType, InputSizeType, InputType } from '../../../types';

const FieldFocusState = { Idle: 'idle', Auto: 'auto', Engaged: 'engaged' } as const;
type FieldFocusState = (typeof FieldFocusState)[keyof typeof FieldFocusState];

const PROGRAMMATIC_FOCUS_ORIGIN: FocusOrigin = 'program';

export type InputFieldValue = string | number | null;

@Component({
  selector: 'fr8bot-ui-input-field',
  imports: [UiInputFocusDirective, ValidationErrorUIComponent, IconUIComponent],
  templateUrl: './input-field.component.html',
  styleUrl: './input-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputFieldComponent
  extends UiFieldControl
  implements FormValueControl<InputFieldValue>
{
  readonly value = model<InputFieldValue>(null);

  readonly id = input(getRandomId('input'));
  // Not `name`: `[formField]` owns that input and would overwrite the autofill hint.
  readonly inputName = input<string | null>(null);
  readonly autocomplete = input<InputAutocompleteType | null>(null);
  readonly label = input<string | null>(null);
  /** Short helper text on the label's line, at the right ("Already verified and can't be edited"). */
  readonly hint = input<string | null>(null);
  readonly placeholder = input('');
  readonly size = input<InputSizeType>('large');
  readonly type = input<InputType>('text');
  readonly isFocused = input(false);
  readonly isReadonly = input(false);
  readonly isInvalid = input(false);
  readonly prefixText = input<string | null>(null);
  readonly postfixText = input<string | null>(null);
  readonly prefixIcon = input<IconsInitConfigType | null>(null);
  readonly postfixIcon = input<IconsInitConfigType | null>(null);
  readonly setNoPadding = input(false);

  protected readonly errorRef = contentChild(ValidationErrorUIComponent);

  protected readonly inputType = computed<InputType>(() =>
    this.isReadonly() ? 'button' : this.type(),
  );
  protected readonly isInvalidShown = computed(() => this.isInvalid() || this.isErrorShown());

  // Keeps what the user typed while it still parses to the model value, so a partial number
  // such as "1." is not rewritten to "1" on the next render.
  protected readonly displayValue = linkedSignal<InputFieldValue, string>({
    source: this.value,
    computation: (value, previous) =>
      previous && this.parse(previous.value) === value ? previous.value : String(value ?? ''),
  });

  private readonly focusMonitor = inject(FocusMonitor);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  // A focus the user didn't make (autofocus through `isFocused`) is not their choice, so leaving the
  // field before engaging with it must not mark it touched and flag it invalid. Once the user
  // engaged, a programmatic focus only follows their action (an autocomplete opening its list).
  private focusState: FieldFocusState = FieldFocusState.Idle;

  // FocusMonitor tells autofocus from the user's focus, and restores the previous origin on window
  // refocus. It runs only while an `isFocused` field may still be autofocused, so other fields carry
  // no `cdk-*-focused` classes.
  private focusOrigin: Signal<FocusOrigin> | null = null;

  constructor() {
    super();
    // Component effects run before the template's, so monitoring starts before the focus directive.
    effect(() => {
      if (this.isFocused() && this.focusState !== FieldFocusState.Engaged) {
        untracked(() => this.startFocusMonitoring());
      }
    });
    inject(DestroyRef).onDestroy(() => this.stopFocusMonitoring());
  }

  protected onFocus(): void {
    if (this.focusOrigin?.() !== PROGRAMMATIC_FOCUS_ORIGIN) {
      this.engage();
    } else if (this.focusState !== FieldFocusState.Engaged) {
      this.focusState = FieldFocusState.Auto;
    }
  }

  // A press or key on a postfix control (the autocomplete arrow) engages the field before it focuses
  // the input programmatically; during autofocus it only leaves the field, as any other click.
  protected onWrapperInteraction(): void {
    if (this.focusState === FieldFocusState.Idle) {
      this.engage();
    }
  }

  // Clicking the autofocused input or its label fires no new focus event, yet it is the user's choice.
  protected engage(): void {
    this.focusState = FieldFocusState.Engaged;
    this.stopFocusMonitoring();
  }

  protected onBlur(): void {
    if (this.focusState === FieldFocusState.Auto) {
      this.focusState = FieldFocusState.Idle;

      return;
    }

    this.touch.emit();
  }

  protected onInput(element: HTMLInputElement): void {
    this.engage();

    // Whitespace is never valid inside an email address, so strip it as it is typed or pasted
    // instead of letting an invisible space fail validation.
    const raw = this.type() === InputType.Email ? element.value.replace(/\s/g, '') : element.value;

    if (raw !== element.value) {
      element.value = raw;
    }

    this.displayValue.set(raw);
    this.value.set(this.parse(raw));
  }

  private parse(raw: string): InputFieldValue {
    if (this.type() !== InputType.Number) {
      return raw;
    }

    const parsed = parseFloat(raw);

    return Number.isNaN(parsed) ? null : parsed;
  }

  private startFocusMonitoring(): void {
    this.focusOrigin ??= toSignal(this.focusMonitor.monitor(this.host, true), {
      initialValue: null,
      injector: this.injector,
    });
  }

  private stopFocusMonitoring(): void {
    if (this.focusOrigin) {
      this.focusMonitor.stopMonitoring(this.host);
      this.focusOrigin = null;
    }
  }
}
